import { type FieldSize, Icon } from "@mdotm/shadcn/legacy/components";
import {
	type ClassList,
	type NodeOrFn,
	renderNodeOrFn,
	toClassListRecord,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import {
	type DataAttributesProps,
	type DropdownDropdownItem,
	type DropdownTriggerProps,
	type ButtonPalette,
	Button,
	DropdownMenu,
} from "@mdotm/shadcn/mdotm/components";
import { useAsync, asyncStateFrom, type AsyncStateId, type MaybeAsync } from "@mdotm/utils/promise";
import { useState } from "react";

type SplitButtonMainAction =
	| { onClick(): void; onClickAsync?: undefined }
	| { onClick?: undefined; onClickAsync: MaybeAsync<void, []> };

type SplitButtonTriggerExtraProps = {
	children?: NodeOrFn;
	label?: string;
	palette?: ButtonPalette;
	size?: FieldSize;
	disabled?: boolean;
	className?: ClassList;
	mainAction: SplitButtonMainAction;
	buttonDataAttributes?: DataAttributesProps;
	chevronDataAttributes?: DataAttributesProps;
};

/**
 * The full split button used as the dropdown trigger: the main {@link Button} carrying the
 * default action (its own `useAsync` pending state merged with the dropdown's aggregated `state`,
 * so a pending action — main or dropdown — always shows on it), joined to a chevron that opens the
 * menu and is merely disabled while loading. Defined at module level so its identity is stable
 * across renders (see {@link DropdownMenu}).
 */
function SplitButtonTrigger({
	state,
	children,
	label,
	palette,
	size,
	disabled,
	className,
	mainAction,
	buttonDataAttributes,
	chevronDataAttributes,
	...chevronTriggerProps
}: DropdownTriggerProps & SplitButtonTriggerExtraProps): React.JSX.Element {
	const [runMainAction, mainActionState] = useAsync<void>(async () => {
		await mainAction.onClickAsync?.();
	});
	// The main button mirrors whichever action settled most recently — its own action or a dropdown
	// action — so a stale error/success from one source doesn't linger once the other updates ("the
	// latest state is the source of truth"). We track the previous value of each source and adopt
	// whichever just changed (React's "adjust state during render" pattern — no effect lag).
	const [tracked, setTracked] = useState<{ dropdown: AsyncStateId; main: AsyncStateId; display: AsyncStateId }>({
		dropdown: state,
		main: mainActionState.kind,
		display: "initial",
	});
	if (tracked.dropdown !== state || tracked.main !== mainActionState.kind) {
		setTracked({
			dropdown: state,
			main: mainActionState.kind,
			display: tracked.dropdown !== state ? state : mainActionState.kind,
		});
	}
	const displayState = tracked.display;
	const loading = displayState === "loading";
	return (
		<div className={toClassName({ flex: true, ...toClassListRecord(className) })}>
			<Button
				disabled={disabled}
				onClick={mainAction.onClickAsync ? unpromisify(runMainAction) : () => mainAction.onClick?.()}
				state={asyncStateFrom(displayState, {
					progress: mainActionState.kind === "loading" ? mainActionState.progress : null,
				})}
				progressBar={mainActionState.kind === "loading" && typeof mainActionState.progress === "number"}
				palette={palette}
				size={size}
				style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
				{...(buttonDataAttributes ?? {})}
			>
				{children ? renderNodeOrFn(children) : label}
			</Button>
			<Button
				{...chevronTriggerProps}
				disabled={disabled || loading}
				palette={palette}
				size={size}
				style={{
					borderTopLeftRadius: 0,
					borderBottomLeftRadius: 0,
					borderLeftWidth: 1,
					borderLeftColor: "rgba(0, 0, 0, 0.15)",
				}}
				{...(chevronDataAttributes ?? {})}
			>
				<span className="transition-transform data-[state=open]:transform-[rotateX(180deg)] flex">
					<Icon icon="Down" />
				</span>
			</Button>
		</div>
	);
}

export type ButtonWithDropdownProps = {
	children?: NodeOrFn;
	/** Convenience label for the main button when `children` is not provided. */
	label?: string;
	palette?: ButtonPalette;
	size?: FieldSize;
	disabled?: boolean;
	className?: ClassList;
	/**
	 * Alternative actions listed in the dropdown. Each item drives its own behavior:
	 * an `onClick` (synchronous, e.g. opens a dialog) or `onClickAsync` (the item and the
	 * trigger reflect its pending state) — see {@link DropdownDropdownItem}.
	 */
	actions: DropdownDropdownItem[] | Array<[title: string, items: DropdownDropdownItem[]]>;
	dataAttributes?: {
		buttonDataAttributes?: DataAttributesProps;
		dropdownTriggerDataAttributes?: DataAttributesProps;
	};
} & (
	| {
			onClick(): void;
			onClickAsync?: undefined;
	  }
	| {
			onClick?: undefined;
			onClickAsync: MaybeAsync<void, []>;
	  }
);

/**
 * A split button: a main {@link Button} carrying the default action, joined to a
 * dropdown ({@link DropdownMenu}) of alternative actions. Mirrors `ButtonWithSelect`'s look,
 * but the dropdown is an action menu (per-item `onClick`/`onClickAsync`) rather than a value picker.
 */
export function ButtonWithDropdown(props: ButtonWithDropdownProps): React.JSX.Element {
	const noActions = !props.actions || (Array.isArray(props.actions) && props.actions.length === 0);

	if (noActions) {
		return (
			<div className={toClassName({ flex: true, ...toClassListRecord(props.className) })}>
				<Button
					disabled={props.disabled}
					{...(props.onClickAsync
						? { onClickAsync: () => props.onClickAsync() }
						: { onClick: () => props.onClick?.() })}
					palette={props.palette}
					size={props.size}
					{...(props.dataAttributes?.buttonDataAttributes ?? {})}
				>
					{props.children ? renderNodeOrFn(props.children) : props.label}
				</Button>
			</div>
		);
	}

	const mainAction: SplitButtonMainAction = props.onClickAsync
		? { onClickAsync: props.onClickAsync }
		: { onClick: props.onClick };

	return (
		<DropdownMenu<SplitButtonTriggerExtraProps>
			Trigger={SplitButtonTrigger}
			extraTriggerProps={{
				children: props.children,
				label: props.label,
				palette: props.palette,
				size: props.size,
				disabled: props.disabled,
				className: props.className,
				mainAction,
				buttonDataAttributes: props.dataAttributes?.buttonDataAttributes,
				chevronDataAttributes: props.dataAttributes?.dropdownTriggerDataAttributes,
			}}
			actions={props.actions}
			disabled={props.disabled}
		/>
	);
}
